import React, { useEffect, useRef } from 'react';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { usePrevious, useUpdateEffect } from 'react-use';
import { replaceUmlauts } from '../../utils';
import { isNumeric } from '../../utils/string';
import { Button, EButtonType } from '../Button';
import { AddressIcon, CantonsIcon, IDsIcon, ImportIcon, PlacesIcon, TrafficIcon, ZipIcon } from '../Icons';
import { RADIUS_UNIT_TYPE, RadiusInput } from '../RadiusInput';
import ReactTags from '../Tags/ReactTags';

export const searchGroups = {
  place: <PlacesIcon />,
  address: <AddressIcon />,
  canton: <CantonsIcon />,
  region: <CantonsIcon />,
  trafficcomp: <TrafficIcon />,
  ids: <IDsIcon />,
  zip: <ZipIcon />,
};

interface Props {
  onSelected: any;
  onUpdate?: any;
  onRemoved?: any;
  getProduct?: any;
  locations?: any;
  placeholder?: string;
  highlightedLocation?: string;
  onHighlightLocation?: any;
  hideImport?: boolean;
  restrictedOptionGroups?: Array<string>;
  updateMapCenter?: any;
  getSearchResults: any;
  triggerImportModal: () => void;
  importButtonLabel: string | React.ReactNode;
  radiusLabel: string | React.ReactNode;
  metersLabel: string | React.ReactNode;
  kilometersLabel: string | React.ReactNode;
  translations: {
    places?: {
      label: string | React.ReactNode;
      description: string | React.ReactNode;
    };
    address?: {
      label: string | React.ReactNode;
      description: string | React.ReactNode;
    };
    cantons?: {
      label: string | React.ReactNode;
      description: string | React.ReactNode;
    };
    region?: {
      label: string | React.ReactNode;
      description: string | React.ReactNode;
    };
    traffic?: {
      label: string | React.ReactNode;
      description: string | React.ReactNode;
    };
    postal?: {
      label: string | React.ReactNode;
      description: string | React.ReactNode;
    };
    id?: {
      label: string | React.ReactNode;
      description: string | React.ReactNode;
    };
    [key: string]: {
      label: string | React.ReactNode;
      description: string | React.ReactNode;
    };
  };
}

interface Suggestion {
  lat?: number;
  lng?: number;
  types: string;
  name: string;
  id: string | number;
  group: string;
}

const spinnerInitialState = {
  place: false,
  address: false,
  canton: false,
  region: false,
  trafficcomp: false,
  postal_code: false,
  id: false,
};

export const LocationSearchInput = (props: Props) => {
  const [address, setAddress] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<any>(spinnerInitialState);
  const [selected, setSelected] = React.useState<any>({});
  const [hideImportedTag, setHideImportedTag] = React.useState<any>(true);
  const [radius, setRadius] = React.useState<number>(1);
  const [radiusUnit, setRadiusUnit] = React.useState<any>('km');

  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth < 767);
  const [addingIdx, setAddingIdx] = React.useState<number>(0);
  const [backendSuggestions, setBackendSuggestions] = React.useState<Suggestion[]>([]);
  const [preSelectedTag, setPreSelectedTag] = React.useState<any>(null);
  const [tags, setTags] = React.useState<any[]>([
    ...Object.keys(props.locations).map((loc, idx) => {
      return {
        id: idx + 1,
        name: unescape(loc),
        data: props.locations[loc],
        urn: props.locations[loc]?.urn,
      };
    }),
  ]);

  const reactTags = useRef<any>(null);
  const prevLocations = usePrevious(props.locations);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 767) {
        setIsMobile(true);
      } else {
        setIsMobile(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const OPTION_GROUPS = [
    {
      id: 'place',
      label: props.translations.places.label,
      description: props.translations.places.description,
      icon: searchGroups['place'],
    },
    {
      id: 'address',
      label: props.translations.address.label,
      description: props.translations.address.description,
      icon: searchGroups['address'],
    },
    {
      id: 'canton',
      label: props.translations.cantons.label,
      description: props.translations.cantons.description,
      icon: searchGroups['canton'],
    },
    {
      id: 'region',
      label: props.translations.region.label,
      description: props.translations.region.description,
      icon: searchGroups['region'],
    },
    {
      id: 'trafficcomp',
      label: props.translations.traffic.label,
      description: props.translations.traffic.description,
      icon: searchGroups['trafficcomp'],
    },
    {
      id: 'postal_code',
      label: props.translations.postal.label,
      description: props.translations.postal.description,
      icon: searchGroups['zip'],
    },
    {
      id: 'id',
      label: props.translations.id.label,
      description: props.translations.id.description,
      icon: searchGroups['ids'],
    },
  ];

  useUpdateEffect(() => {
    if (prevLocations != props.locations) {
      const newTags = [
        ...Object.keys(props.locations).map((loc, idx) => {
          return {
            id: idx + 1,
            name: unescape(loc),
            data: props.locations[loc],
            urn: props.locations[loc]?.urn,
          };
        }),
      ];

      setTags(newTags);
    }
  }, [props.locations]);

  const lastSearchTermRef = useRef('');

  const getSearchResultsFunc = (address: string, context: string) => {
    if (address !== lastSearchTermRef.current) {
      if (currentAbortController.current) {
        currentAbortController.current.abort('SEARCH_RESULTS_ABORT');
      }
      currentAbortController.current = new AbortController();
    }

    lastSearchTermRef.current = address;
    const { signal } = currentAbortController.current;

    return props.getSearchResults({
      context: context,
      searchterm: address,
      signal: signal,
    });
  };

  const currentAbortController = useRef(null);

  const handleChange = (address: string) => {
    setAddress(address);

    if ((isNumeric(address) && address.length > 0) || address.length > 1) {
      const searchesByContext = [];

      let spinner = { ...spinnerInitialState };
      if (preSelectedTag) {
        spinner[preSelectedTag.group] = true;
        if (preSelectedTag.group === 'id') {
          searchesByContext.push(getSearchResultsFunc(address, 'id'));
        }
        if (preSelectedTag.group === 'trafficcomp') {
          searchesByContext.push(getSearchResultsFunc(address, 'trafficcomp'));
        }
        if (preSelectedTag.group === 'canton') {
          searchesByContext.push(getSearchResultsFunc(address, 'canton'));
        }
        if (preSelectedTag.group === 'region') {
          searchesByContext.push(getSearchResultsFunc(address, 'region'));
        }
        if (preSelectedTag.group === 'place') {
          searchesByContext.push(getSearchResultsFunc(address, 'place'));
          searchesByContext.push(getSearchResultsFunc(address, 'fraction'));
        }
      } else {
        if (isNumeric(address) && !props.restrictedOptionGroups?.includes('id')) {
          searchesByContext.push(getSearchResultsFunc(address, 'id'));
          spinner['id'] = true;
        } else {
          if (!props.restrictedOptionGroups?.includes('trafficcomp')) {
            searchesByContext.push(getSearchResultsFunc(address, 'trafficcomp'));
            spinner['trafficcomp'] = true;
          }
          if (!props.restrictedOptionGroups?.includes('canton')) {
            searchesByContext.push(getSearchResultsFunc(address, 'canton'));
            spinner['canton'] = true;
          }
          if (!props.restrictedOptionGroups?.includes('region')) {
            searchesByContext.push(getSearchResultsFunc(address, 'region'));
            spinner['region'] = true;
          }
          if (!props.restrictedOptionGroups?.includes('place')) {
            searchesByContext.push(getSearchResultsFunc(address, 'place'));
            spinner['place'] = true;
          }
          if (!props.restrictedOptionGroups?.includes('fraction')) {
            searchesByContext.push(getSearchResultsFunc(address, 'fraction'));
            spinner['fraction'] = true;
          }
        }
      }

      setIsLoading(spinner);
      Promise.all(searchesByContext)
        .then((responses) => {
          const validResponses = responses.filter((response) => response !== null);
          if (validResponses.length > 0) {
            const newSuggestions = [];
            for (const response of validResponses) {
              const context = response.data.context;
              response.data.urnlist.map((item: any) => {
                newSuggestions.push({
                  id: item.urn,
                  name: item.label,
                  types: context,
                  group: context === 'fraction' ? 'place' : context,
                  lat: item.latitude,
                  lng: item.langitude,
                });
                return {};
              });
            }
            setIsLoading(spinnerInitialState);
            setBackendSuggestions(newSuggestions);
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Error fetching data: ', error);
          }
        });
    } else {
      setIsLoading(spinnerInitialState);
    }
  };

  const processTag = (
    tag: {
      types: string;
      id: string;
      name: string;
      group: string;
    },
    _radius?: number,
    _radiusUnit?: string,
  ) => {
    if (typeof tag.types === 'string' && tag.types.includes('product_id') && tag.id && props.getProduct) {
      props
        .getProduct(`it21/af::${tag.id}`)
        .then((result) => {
          if (result?.data) {
            setSelected({
              ...selected,
              [addingIdx]: tag.name,
            });
            props.onSelected(
              address,
              {
                lat: result.data.latitude,
                lng: result.data.longitude,
              },
              tag.types.split(', '),
              result.data,
              'id',
            );

            setAddress('');
          }
        })
        .catch((error) => console.debug('Error: ', error));
    } else if (typeof tag.id === 'string') {
      const dataFromBackend = backendSuggestions.filter((item) => item.id === tag.id)[0];
      let criteria = tag.id.split(':')[1];
      if (tag.id.split(':')[1] === 'trafficcomp') {
        criteria = 'trafficcompany';
      }
      if (dataFromBackend) {
        setSelected({
          ...selected,
          [addingIdx]: tag.name,
        });
        props.onSelected(
          tag.name,
          {
            lat: dataFromBackend.lat,
            lng: dataFromBackend.lng,
          },
          [tag.id.split(':')[1], 'locality'],
          { ...tag, criteria: criteria },
          tag.group === 'region' ? 'region' : tag.id.split(':')[1],
        );
        setAddress('');
      }
    } else if (radius && radiusUnit) {
      const tagName = tag.name.split(',')[0];
      setAddress(tagName);

      geocodeByAddress(tag.name)
        .then(async (results) => {
          const latLng = await getLatLng(results[0]);
          return { latLng: latLng, types: results[0].types };
        })
        .then((_results) => {
          const { latLng, types } = _results;
          setSelected({
            ...selected,
            [addingIdx]: tagName,
          });
          if (tag.group === 'address') {
            props.onSelected(
              tagName,
              latLng,
              types,
              undefined,
              types.includes('locality') ? 'place' : 'address',
              _radius,
              _radiusUnit,
            );
          } else {
            props.onSelected(
              tagName,
              latLng,
              types,
              undefined,
              tag.group === 'postal_code' ? 'postal_code' : types.includes('locality') ? 'place' : 'address',
            );
          }
          setAddress('');
        })
        .catch((error: string) => console.debug('Error', JSON.parse(error)));
    }
  };

  const handleSelect = (tag) => {
    if (preSelectedTag && preSelectedTag.group) {
      const tagName = tag.name.split(',')[0];
      setAddress(tagName);

      geocodeByAddress(tag.name)
        .then(async (results) => {
          const latLng = await getLatLng(results[0]);
          return { latLng: latLng, types: results[0].types };
        })
        .then((_results) => {
          const { latLng, types } = _results;
          setSelected({
            ...selected,
            [addingIdx]: tagName,
          });

          if (preSelectedTag.group === 'address') {
            props.onSelected(
              tagName,
              latLng,
              types,
              undefined,
              'address',
              preSelectedTag.data.circle.radius,
              preSelectedTag.data.circle.radiusUnit,
            );
          } else {
            processTag(tag);
          }
        })
        .catch((error: string) => console.debug('Error', JSON.parse(error)));
    } else {
      processTag(tag, radiusUnit === RADIUS_UNIT_TYPE.KILO_METERS ? radius * 1000 : radius, radiusUnit);
    }

    setAddingIdx(addingIdx + 1);
    setBackendSuggestions([]);
    setAddress('');
  };
  const onDelete = (i: number, b: any) => {
    tags[i] && props.onRemoved(replaceUmlauts(tags[i].name));
    const _tags = tags.slice(0);
    _tags.splice(i, 1);
    setTags(_tags);
  };

  const onAddition = (tag, e) => {
    if (tag?.types === 'preSelectedTag') {
      if (tag.group === 'address') {
        tag.data.circle.radius = radiusUnit === RADIUS_UNIT_TYPE.KILO_METERS ? radius * 1000 : radius;
        tag.data.circle.radiusUnit = radiusUnit;
      }
      setPreSelectedTag(tag);
      return;
    }

    const tagName = escape(tag.name.slice(0, tag.name.lastIndexOf(',')));

    if (!props.locations[tagName]) {
      const _tags = [...tags, tag];
      setTags(_tags);
      handleSelect(tag);
      setBackendSuggestions([]);
      setAddress('');

      if (preSelectedTag) {
        setPreSelectedTag(null);
      }
    }
  };

  const onUpdating = (tag) => {
    if (tag?.types === 'preSelectedTag') {
      if (tag.group === 'address') {
        tag.data.circle.radius =
          tag.data.circle.radiusUnit === RADIUS_UNIT_TYPE.KILO_METERS
            ? tag.data.circle.radius * 1000
            : tag.data.circle.radius;
      }

      setPreSelectedTag({
        ...preSelectedTag,
        data: tag.data,
      });

      return;
    }

    if (tag && tag.data && tag.data.circle) {
      const radius =
        tag.data.circle.radiusUnit === RADIUS_UNIT_TYPE.KILO_METERS
          ? tag.data.circle.radius * 1000
          : tag.data.circle.radius;
      props.onUpdate(tag.name, radius, tag.data.circle.radiusUnit);
    }
  };

  const clearAll = () => {
    tags.forEach((tag, i) => {
      onDelete(i, null);
    });
    setPreSelectedTag(null);
    setBackendSuggestions([]);
    setAddress('');
  };

  const googlePlacesFilteringMap = [
    'ChIJL31ajc1ujkcRLsM3ufbR5bs',
    'ChIJ_____yuvmkcRvDuuoTOvnr8',
    'ChIJF9Y9qb78j0cRUSXaxMLXcDU',
    'ChIJN5aLzd6omkcRMgYlHTyyhnM',
    'ChIJ7Y326MZskEcRQFdiJY87Iys',
    'ChIJM1H8lER7jEcR8ReANchNzhM',
    'ChIJE5cVc4SimkcRci6eIdkmw_k',
    'ChIJWdZa4NsAkEcRpHqSeMlyzdE',
    'ChIJaQ9EyXUtjkcRkR2QHCxIh8U',
    'ChIJJdQTrhlUhUcRojl2JtzTifQ',
    'ChIJOelH2Ua7mkcRTJz4qnrKhcU',
    'ChIJW3mmHEQ8kEcRk-zQHdc_6Zo',
    'ChIJX4EgYSjhmkcRTSHbd0MrcxM',
    'ChIJG6xyH1jhmkcRmcsJq0Got8k',
    'ChIJcXtdhZJ8jEcRHkB0eZ0Y6OY',
    'ChIJmx7q-43HmkcRfHljHmrU0Ss',
    'ChIJvwRgOX_PixARE19LWyQeSVg', // Mels-Africa
    'ChIJnaPj7TbShEcRNvXGKfPSM80', // Mels CH -> Afrika
    'ChIJMXZaq75HkEcRAAEZt1qh1p0', // Frick
    'ChIJ8-RGYCmrmkcREOrjxwO_UKk', // Zug
  ];

  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={handleSelect}
      highlightFirstSuggestion
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      searchOptions={{
        componentRestrictions: { country: ['ch', 'li'] as unknown as string },
      }}
    >
      {({ getInputProps, suggestions }) => {
        let filteredSuggestions = [...suggestions];

        if (filteredSuggestions.map((value) => value.placeId).some((arr) => googlePlacesFilteringMap.includes(arr))) {
          filteredSuggestions = [...filteredSuggestions].filter(
            (value) => !googlePlacesFilteringMap.includes(value.placeId),
          );
        }

        let suggestionsReworked: Suggestion[] = [...filteredSuggestions]
          .map((el) => {
            let group = 'address';
            if (el.types.includes('locality')) {
              group = 'place';
            } else if (el.types.includes('postal_code')) {
              group = 'postal_code';
            } else if (el.types.includes('point_of_interest')) {
              group = 'address';
            }

            return {
              id: el.index,
              name: el.description.toString().replace(', Switzerland', ''),
              types: el.types,
              group: group,
            };
          })
          .filter((el) => {
            if (preSelectedTag) {
              return el.group == preSelectedTag.group;
            } else {
              return el;
            }
          });

        if (preSelectedTag) {
          if (preSelectedTag.group === 'id') {
            if (address.toLocaleLowerCase().includes('id:') && props.getProduct) {
              suggestionsReworked = [
                {
                  id: address.replace(/\s/g, '').slice(3),
                  name: address,
                  types: 'product_id, locality',
                  group: 'id',
                },
                ...suggestionsReworked,
              ];
            } else {
              suggestionsReworked = [];
            }
          }
          if (preSelectedTag.group === 'trafficcomp') {
            suggestionsReworked = suggestionsReworked.filter((suggestion) => suggestion.group === 'trafficcomp');
          }
          if (preSelectedTag.group === 'canton') {
            suggestionsReworked = suggestionsReworked.filter((suggestion) => suggestion.group === 'canton');
          }
          if (preSelectedTag.group === 'region') {
            suggestionsReworked = suggestionsReworked.filter((suggestion) => suggestion.group === 'region');
          }
          if (preSelectedTag.group === 'place') {
            suggestionsReworked = suggestionsReworked.filter((suggestion) => suggestion.group === 'place');
          }

          if (preSelectedTag.group === 'postal_code') {
            suggestionsReworked = suggestionsReworked.filter((suggestion) => suggestion.group === 'postal_code');
          }

          suggestionsReworked = [
            ...suggestionsReworked,
            ...backendSuggestions.filter((suggestion) => suggestion.group === preSelectedTag.group),
          ];
        } else {
          if (isNumeric(address)) {
            // Filter results only for ZIP code if the search string is just numeric
            suggestionsReworked = suggestionsReworked.filter((suggestion) => suggestion.group === 'postal_code');
          }

          if (address.toLocaleLowerCase().includes('id:') && props.getProduct) {
            suggestionsReworked = [
              {
                id: address.replace(/\s/g, '').slice(3),
                name: address,
                types: 'product_id, locality',
                group: 'id',
              },
              ...suggestionsReworked,
            ];
          }

          suggestionsReworked = [...suggestionsReworked, ...backendSuggestions];
        }

        if (!address) {
          suggestionsReworked = [];
        }

        const filteredOptionGroups = OPTION_GROUPS.filter((group) => !props.restrictedOptionGroups?.includes(group.id));

        return (
          <div className='placesAutocomplete'>
            <ReactTags
              onInput={(val) => {
                getInputProps().onChange({ target: { value: val } });
              }}
              onBlur={() => {
                if (!address) {
                  setBackendSuggestions([]);
                  setAddress('');
                  setIsLoading(spinnerInitialState);
                }
              }}
              onChange={(tagVal) => {
                onUpdating(tagVal);
              }}
              clearAll={clearAll.bind(this)}
              ref={reactTags}
              tags={tags}
              hideImportedTag={hideImportedTag}
              setHideImportedTag={setHideImportedTag}
              preSelectedTag={preSelectedTag}
              onDeleteCustomTag={() => {
                setPreSelectedTag(null);
                setBackendSuggestions([]);
                setAddress('');
                setIsLoading(spinnerInitialState);
              }}
              suggestions={suggestionsReworked}
              onDelete={onDelete.bind(this)}
              onAddition={onAddition.bind(this)}
              onTagClick={(urn) => {
                props.onHighlightLocation(urn);
                const urnSplit = urn?.split(':');
                props.updateMapCenter({ lat: Number(urnSplit[3]), lng: Number(urnSplit[2]) });
              }}
              highlightedTags={[props.highlightedLocation]}
              placeholderText={props.placeholder}
              optionsGroups={filteredOptionGroups}
              minQueryLength={0}
              initialScreen={!address && !preSelectedTag}
              isLoading={isLoading}
              maxSuggestionsLength={100}
              isShowGroupDescription={!address}
              rightInnerComponent={
                !props?.hideImport && (
                  <Button
                    buttonType={EButtonType.BasicLink}
                    className={'d-flex align-items-center mt-0'}
                    isSvgStroke={true}
                    isSvgFill={false}
                    onClick={() => {
                      setHideImportedTag(true);
                      props.triggerImportModal();
                      // navigate({ search: '?modalContent=import' });
                    }}
                  >
                    <ImportIcon className='me-3' />
                    {!isMobile && props.importButtonLabel}
                  </Button>
                )
              }
              rightSuggestionsComponent={
                <RadiusInput
                  radius={radius}
                  setRadius={setRadius}
                  radiusUnit={radiusUnit}
                  setRadiusUnit={setRadiusUnit}
                  kilometersLabel={props.kilometersLabel}
                  metersLabel={props.metersLabel}
                  radiusLabel={props.radiusLabel}
                />
              }
              kilometersLabel={props.kilometersLabel}
              metersLabel={props.metersLabel}
              radiusLabel={props.radiusLabel}
            />
          </div>
        );
      }}
    </PlacesAutocomplete>
  );
};
